<template>
    <VForm
        id="global-form"
        ref="validator"
        v-slot="{meta: {valid}, errors}"
        keep-values
        @submit="submit">
        <slot />
        <div class="d-flex justify-content-end mt-4">
            <ValidationButton
                :error-explanation="$t('eventRegistration.someErrorsExplanationRegStepPage')"
                :errors="errors"
                :invalid="!valid">
                <div class="d-flex align-items-end flex-column gap-2">
                    <template v-if="submitBlocked && visibleStepsAfter.length === 0">
                        <NextButton
                            button-type="danger"
                            disabled
                            invalid
                            :next-label="blockSubmitLabel">
                            <FontAwesomeIcon
                                class="mr-2"
                                icon="circle-notch"
                                spin />
                            {{ blockSubmitLabel }}
                        </NextButton>
                        <div class="d-flex flex-column">
                            <small class="text-xs text-muted">
                                <FontAwesomeIcon icon="exclamation-triangle" />
                                {{ $t('eventRegistration.submitBlockedExplanation') }}
                            </small>
                            <small class="text-xs text-muted">
                                {{ $t('eventRegistration.submitBlockedExplanationSecond') }}
                            </small>
                            <small class="text-xs text-muted">
                                {{ $t('eventRegistration.submitBlockedExplanationThird') }}
                            </small>
                        </div>
                    </template>
                    <PaypalButton
                        v-else-if="isFinalBookingStepAndPaypalButtonShouldBeDisplayed"
                        :event-id="eventData.id"
                        :payment-type-id="selectedPaymentType.id"
                        :client-id="selectedPaymentType.settings.client_id"
                        :price-to-pay="getInvoiceTotal - getCouponTotal"
                        :articles-grouped="getAllInvoiceArticlesGroupedByArticle"
                        :coupon-total="getCouponTotal"
                        :currency="eventData.currency"
                        :label="nextLabel"
                        :disabled="!valid"
                        @mouseover="$refs.validator.validate()"
                        @authorizing="loading = true"
                        @authorized="saveAuthorizationAndSubmit"
                        @unauthorized="loading = false" />
                    <NextButton
                        v-else
                        :button-type="visibleStepsAfter.length === 0 ? 'danger' : 'default'"
                        :invalid="!valid"
                        :next-label="nextLabel"
                        @mouseover="$refs.validator.validate()" />
                </div>
            </ValidationButton>
        </div>
        <StoreValidationTransmitter
            :step-name="stepName"
            :valid="valid" />
        <div
            v-show="loading"
            class="loading-screen">
            <div
                class="d-flex justify-content-center align-items-center"
                style="height: 100vh">
                <FontAwesomeIcon
                    icon="circle-notch"
                    spin
                    size="8x"
                    class="text-primary" />
            </div>
        </div>
    </VForm>
</template>

<script setup>


import {onBeforeRouteLeave} from "vue-router";
import cloneDeep from "lodash/cloneDeep";

const props = defineProps({
    stepName: {
        type: String,
        required: true
    }
})
const {t: $t} = useI18n()
const {$store, $moment} = useNuxtApp()
const tooManyAttemptsAt = computed(() => $store.state.registration.tooManyAttemptsAt)
const retryAfter = computed(() => $store.state.registration.retryAfter)

function isSubmitBlocked() {
    const steps = cloneDeep($store.getters['registration/getRegistrationSteps'])
    const activeStepIndex = steps.findIndex(step => step.name === props.stepName)
    const stepsAfter = steps.splice(activeStepIndex + 1)
    if(stepsAfter.some(step => !step.hidden)) return false

    const submitBlockDiff = tooManyAttemptsAt.value && $moment().diff(tooManyAttemptsAt.value, 'seconds')
    return submitBlockDiff !== null && submitBlockDiff < (retryAfter.value ?? 60)
}

function preventNav(event) {
    if (isSubmitBlocked()) {
        event.preventDefault()
        event.returnValue = $t('eventRegistration.dontLeaveOpenRegistration')
    }
}

onBeforeRouteLeave(useRouteLeaveWarning(
    computed(() => isSubmitBlocked()), $t('eventRegistration.dontLeaveOpenRegistration')
))

onBeforeMount(() => {
    window.addEventListener("beforeunload", preventNav)
})

onBeforeUnmount(() => {
    window.removeEventListener("beforeunload", preventNav)
})
</script>

<script>
import registrationStepsAndValidationMixin from "@/mixins/registration/registrationStepsAndValidationMixin";
import registrationPaymentMixin from "@/mixins/registration/registrationPaymentMixin";
import registrationPriceMixin from "@/mixins/registration/registrationPriceMixin";
import registrationGroupMixin from "@/mixins/registration/registrationGroupMixin";
import registrationOnlineRegistrationFieldsMixin from "@/mixins/registration/registrationOnlineRegistrationFieldsMixin";
import {computed} from 'vue'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
    name: "RegistrationStepPage",
    components: {FontAwesomeIcon},
    mixins: [registrationStepsAndValidationMixin, registrationPaymentMixin, registrationPriceMixin, registrationGroupMixin, registrationOnlineRegistrationFieldsMixin],
    provide() {
        return {
            stepName: this.stepName,
            globalValidator: computed(() => this.$refs.validator)
        }
    },
    props: {
        stepName: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            submitBlockDiff: null
        }
    },
    computed: {
        nextLabel() {
            if (!this.visibleStepsAfter.length) {
                if (this.isGroupRegistrar && !this.registrationData.finishGroupRegistration) {
                    if (this.changeWordingRegistrationBuy) {
                        return this.$t('eventRegistration.saveGroupBuy')
                    } else {
                        return this.$t('eventRegistration.saveGroupRegistration')
                    }
                } else {
                    if (this.changeWordingRegistrationBuy) {
                        return this.$t('eventRegistration.buyNow')
                    } else {
                        if (this.chargeable)
                            return this.$t('eventRegistration.bookNowPayable')
                        else
                            return this.$t('eventRegistration.bookNow')
                    }
                }
            }
            return null
        },
        submitBlocked() {
            return this.submitBlockDiff !== null && this.submitBlockDiff < (this.retryAfter ?? 60)
        },
        blockSubmitLabel() {
            if (!this.submitBlocked) return null
            return this.$t('eventRegistration.submitBlocked', {seconds: (this.retryAfter ?? 60) - this.submitBlockDiff})
        },
        chargeable() {
            return this.$store.getters['registration/chargeable']
        },
        paypalIsSelected() {
            return this.selectedPaymentType?.type.id === 3
        },
        isFinalBookingStepAndPaypalButtonShouldBeDisplayed() {
            if (!this.$route.name.includes('regSummary')) return false
            if (this.visibleStepsAfter && this.visibleStepsAfter.length > 0) return false
            if (this.isGroupRegistrar && !this.registrationData.finishGroupRegistration)
                return false
            return this.chargeable && this.paypalIsSelected;
        },
        stepsValidationSectionForThisStep() {
            if (!this.stepsValidationSections) return {}
            return this.stepsValidationSections[this.stepName] || {}
        }
    },
    methods: {
        submit() {
            if (this.visibleStepsAfter && this.visibleStepsAfter.length > 0) {
                this.nextStep()
            } else if (!this.submitBlocked) {
                this.sendRegistration()
            }
        },
        saveAuthorizationAndSubmit(event) {
            if (!event.orderId || !event.authorizationId) return
            this.paypalOrderId = event.orderId
            this.paypalAuthorizationId = event.authorizationId
            this.paypalAuthorizationAmount = event.authorizationAmount
            this.sendRegistration()
        },
        blockSubmitClock() {
            if (this.visibleStepsAfter && this.visibleStepsAfter.length > 0) return
            this.submitBlockDiff = this.tooManyAttemptsAt && this.$moment().diff(this.tooManyAttemptsAt, 'seconds')
            if (this.submitBlocked) {
                setTimeout(() => this.blockSubmitClock(), 1000)
            }
        }
    },
    watch: {
        tooManyAttemptsAt: {
            handler() {
                if (this.tooManyAttemptsAt && this.$moment().diff(this.tooManyAttemptsAt, 'seconds') < (this.retryAfter ?? 60))
                    this.blockSubmitClock()
            },
            immediate: true
        }
    }
}
</script>
<style lang="scss">
.loading-screen {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(white, 0.5);
    z-index: 10;
}
</style>

